import { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ThemeProvider } from "next-themes";

// Pages
import Landing from "@/pages/Landing";
import Dashboard from "@/pages/Dashboard";
import Products from "@/pages/Products";
import ProductForm from "@/pages/ProductForm";
import Suppliers from "@/pages/Suppliers";
import SupplierForm from "@/pages/SupplierForm";
import Locations from "@/pages/Locations";
import Inventory from "@/pages/Inventory";
import ReceivingSessions from "@/pages/ReceivingSessions";
import ReceivingSession from "@/pages/ReceivingSession";
import NewReceivingSession from "@/pages/NewReceivingSession";
import Reports from "@/pages/Reports";
import AuditLogs from "@/pages/AuditLogs";
import BarcodeGenerator from "@/pages/BarcodeGenerator";
import Sellers from "@/pages/Sellers";
import Sales from "@/pages/Sales";
import Marketplace from "@/pages/Marketplace";
import Scanner from "@/pages/Scanner";
import Alerts from "@/pages/Alerts";
import UserManagement from "@/pages/UserManagement";
import Forecast from "@/pages/Forecast";
import AiAssistant from "@/components/AiAssistant";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Extract session_id from URL (hash or query params)
function getSessionId() {
  // Check hash first: #session_id=xxx or #...&session_id=xxx
  const hash = window.location.hash;
  if (hash && hash.includes("session_id=")) {
    const match = hash.match(/session_id=([^&]+)/);
    if (match) return match[1];
  }
  // Fallback: check query params ?session_id=xxx
  const params = new URLSearchParams(window.location.search);
  const qsId = params.get("session_id");
  if (qsId) return qsId;
  return null;
}

// Check if URL contains session_id (synchronous, for render-time checks)
function hasSessionIdInUrl() {
  return !!(window.location.hash?.includes("session_id=") ||
    new URLSearchParams(window.location.search).get("session_id"));
}

// Auth Callback Component
// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  const processAuth = async (retryCount = 0) => {
    const sessionId = getSessionId();

    if (!sessionId) {
      console.error("AuthCallback: No session_id found in URL");
      navigate("/", { replace: true });
      return;
    }

    try {
      const response = await fetch(`${API}/auth/session`, {
        headers: { "X-Session-ID": sessionId },
        credentials: "include"
      });

      if (!response.ok) {
        const errBody = await response.text();
        // Retry once on failure (session might need a moment)
        if (retryCount < 1) {
          console.log("Auth retry...");
          await new Promise(r => setTimeout(r, 1500));
          return processAuth(retryCount + 1);
        }
        throw new Error(`Auth failed: ${response.status} ${errBody}`);
      }

      const user = await response.json();
      // Clear the hash/query to prevent re-processing
      window.history.replaceState(null, "", window.location.pathname);
      navigate("/dashboard", { replace: true, state: { user } });
    } catch (err) {
      console.error("Auth error:", err);
      setError(true);
    }
  };

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    processAuth();
  }, []);

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="auth-error-screen">
        <div className="text-center max-w-md space-y-4">
          <div className="mx-auto h-16 w-16 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
            <svg className="h-8 w-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold">Login Session Expired</h1>
          <p className="text-muted-foreground">
            Your login session timed out. This can happen if the page takes too long to load. Please try again.
          </p>
          <div className="flex flex-col gap-2">
            <button
              onClick={() => {
                const redirectUrl = window.location.origin + "/dashboard";
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
              }}
              className="inline-flex items-center justify-center rounded-md bg-primary text-primary-foreground px-6 py-3 text-sm font-medium hover:bg-primary/90"
              data-testid="retry-login-btn"
            >
              Try Again
            </button>
            <button
              onClick={() => navigate("/", { replace: true })}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Authenticating...</p>
      </div>
    </div>
  );
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [pendingApproval, setPendingApproval] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    // Skip if user passed from AuthCallback
    if (location.state?.user) {
      const u = location.state.user;
      if (u.status === "pending" || u.status === "suspended") {
        setPendingApproval(true);
        setUser(u);
        setIsAuthenticated(true);
        return;
      }
      setUser(u);
      setIsAuthenticated(true);
      return;
    }

    const checkAuth = async () => {
      try {
        const response = await fetch(`${API}/auth/me`, {
          credentials: "include"
        });
        if (!response.ok) throw new Error("Not authenticated");
        const userData = await response.json();
        if (userData.status === "pending" || userData.status === "suspended") {
          setPendingApproval(true);
          setUser(userData);
          setIsAuthenticated(true);
          return;
        }
        setUser(userData);
        setIsAuthenticated(true);
      } catch (error) {
        setIsAuthenticated(false);
        navigate("/", { replace: true });
      }
    };

    checkAuth();
  }, [location.state, navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  if (pendingApproval) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background px-4" data-testid="pending-approval-screen">
        <div className="text-center max-w-md space-y-4">
          <div className="mx-auto h-16 w-16 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
            <svg className="h-8 w-8 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold">
            {user?.status === "suspended" ? "Account Suspended" : "Pending Approval"}
          </h1>
          <p className="text-muted-foreground">
            {user?.status === "suspended"
              ? "Your account has been suspended. Please contact an administrator."
              : `Welcome, ${user?.name || ""}! Your account has been created. An administrator needs to approve your access before you can use the system.`}
          </p>
          <p className="text-sm text-muted-foreground">
            Contact: <a href="mailto:support@ipvaluemanagements.com" className="text-primary hover:underline">support@ipvaluemanagements.com</a>
          </p>
          <button
            onClick={() => {
              fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
              navigate("/", { replace: true });
            }}
            className="text-sm text-muted-foreground hover:text-foreground underline"
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return children({ user });
};

// App Router with session_id detection
function AppRouter() {
  const location = useLocation();

  // Check URL for session_id synchronously during render (hash or query params)
  if (hasSessionIdInUrl()) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            {({ user }) => <Dashboard user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/products"
        element={
          <ProtectedRoute>
            {({ user }) => <Products user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/new"
        element={
          <ProtectedRoute>
            {({ user }) => <ProductForm user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/products/:id/edit"
        element={
          <ProtectedRoute>
            {({ user }) => <ProductForm user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers"
        element={
          <ProtectedRoute>
            {({ user }) => <Suppliers user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers/new"
        element={
          <ProtectedRoute>
            {({ user }) => <SupplierForm user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/suppliers/:id/edit"
        element={
          <ProtectedRoute>
            {({ user }) => <SupplierForm user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/locations"
        element={
          <ProtectedRoute>
            {({ user }) => <Locations user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/inventory"
        element={
          <ProtectedRoute>
            {({ user }) => <Inventory user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/receiving"
        element={
          <ProtectedRoute>
            {({ user }) => <ReceivingSessions user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/receiving/new"
        element={
          <ProtectedRoute>
            {({ user }) => <NewReceivingSession user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/receiving/:id"
        element={
          <ProtectedRoute>
            {({ user }) => <ReceivingSession user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/reports"
        element={
          <ProtectedRoute>
            {({ user }) => <Reports user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute>
            {({ user }) => <AuditLogs user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/barcodes"
        element={
          <ProtectedRoute>
            {({ user }) => <BarcodeGenerator user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sellers"
        element={
          <ProtectedRoute>
            {({ user }) => <Sellers user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/sales"
        element={
          <ProtectedRoute>
            {({ user }) => <Sales user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            {({ user }) => <Marketplace user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/scanner"
        element={
          <ProtectedRoute>
            {({ user }) => <Scanner user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/alerts"
        element={
          <ProtectedRoute>
            {({ user }) => <Alerts user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/users"
        element={
          <ProtectedRoute>
            {({ user }) => <UserManagement user={user} />}
          </ProtectedRoute>
        }
      />
      <Route
        path="/forecast"
        element={
          <ProtectedRoute>
            {({ user }) => <Forecast user={user} />}
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
      <div className="App">
        <BrowserRouter>
          <AppRouter />
        </BrowserRouter>
        <AiAssistant />
        <Toaster position="top-right" richColors />
      </div>
    </ThemeProvider>
  );
}

export default App;
